import _ from 'lodash'
import {
  Box,
  Button,
  Container,
  Grid,
  makeStyles,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { ChevronLeft, ChevronRight, Settings } from 'react-feather'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { projectVar } from '../../../apollo/reactive-vars'
import {
  EnergySystemType,
  HourSeriesFragment,
  UpdateBuildingEnergyModelInput,
  type BuildingEnergyModelFragment,
} from '../../../generated/graphql'
import { theme } from '../../../theme'
import { calibrationScheduleFactors } from './calibration-util'
import { CalibrationDataDialog } from './dialog/CalibrationDataDialog'
import {
  CalibrationFormValues,
  defaultCalibrationValues,
  mapCalibrationFormValuesToInput,
  mapInputToCalibrationFormValues,
} from './dialog/calibration-form-values'
import { useAuthorization } from '../../../authorization/use-authorization'
import {
  calcDhwCirculationLossChartData,
  calcDhwConsumtionInSummerByHourChartData,
  calcSumDhwHeatingInYearByMonth,
} from './dhw-calibration-util'
import { LineGraphContent } from './graph-content/LineGraphContent'
import {
  calcHeatingByEvenTempRange,
  calcHeatingByHour,
  calcHeatingDifferenceByEvenTempRange,
  calcHeatingDifferencesByHour,
  calcNetHeatingInFirstWeekByHour,
  calcNetHeatingInYearByHour,
  calcSortedNetHeatingInYearByHour,
  calcSumHeatingInYearByMonth,
  calcSumNetEnergyOfYear,
  CHART_TEMP_END,
  CHART_TEMP_START,
  CHART_TEMP_STEP,
} from './chart-data-calculation-util'
import {
  calcDhwMeasuredDataInSummerByHour,
  calcDhwMeasuredDataInWinterTempByHour,
  calcDhwMeasuredDataInZeroTempByHour,
  calcMeasuredHeatingDataInFirstWeekByHour,
  calcMeasuredHeatingDataInYearByHour,
  calcSumMeasuredHeatingDataInYearByMonth,
  calcMeasuredHeatingInDayByEvenTempRange,
  calcMeasuredHeatingInNightByEvenTempRange,
  calcSortedMeasuredHeatingDataInYearByHour,
  calcMeasuredCoolingDensity,
  calcSortedMeasuredCoolingDensityInYearByHour,
  calcSumMeasuredCoolingDataInYearByMonth,
  calcSortedMeasuredElectricityDensityInYearByHour,
  calcMeasuredElectricityDataByHour,
  calcSumMeasuredElectricityDataInYearByMonth,
  calcMeasuredElectricityInDayByEvenTempRange,
} from './measured-values-calibration-util'
import { CalibrationChartType } from './chart-util'
import { BarGraphContent } from './graph-content/BarGraphContent'
import {
  calcNetCoolingByTempChartData,
  calcSortedCoolingDensityInYearByHourChartData,
  calcSumCoolingInYearByMonthChartData,
} from './cooling-calibration-util'
import {
  calcElectricityInYearByHourChartData,
  calcSortedNetElectricityDensityInYearByHourChartData,
  calcSumElectricityInYearByMonthChartData,
} from './electricity-calibration-util'
import { GraphData, initGraphData } from './chart-data-util'

const useStyles = makeStyles((themeSpec) => ({
  header: {
    marginTop: themeSpec.spacing(4),
    marginBottom: themeSpec.spacing(2),
  },
  subtitle1: {
    color: themeSpec.palette.grey[500],
    textTransform: 'none',
  },
  skipUppercase: {
    textTransform: 'none',
  },
  browsingButton: {
    minWidth: 0,
    border: 'solid',
    borderWidth: 'thin',
    borderColor: 'lightgray',
    margin: '1px',
  },
  stepper: {
    marginTop: themeSpec.spacing(4),
    marginBottom: themeSpec.spacing(4),
    padding: 0,
    backgroundColor: 'unset',
  },
}))

const heatingColor = '#fd8d3c'
const coolingColor = '#4292c6'
const electricityColor = '#0165fc'

const useStepConnectorStyles = makeStyles({
  stepConnector: (steps: GraphData[]) => {
    const styles: { [key: string]: CSSProperties } = {}

    steps.forEach(({ id }) => {
      if (id < steps.length - 1) {
        let connectorColor = 'lightgray'
        if (id < 12) connectorColor = heatingColor
        else if (id > 12 && id < 15) connectorColor = coolingColor
        else if (id > 15 && id < 19) connectorColor = electricityColor

        styles[`&:nth-child(${2 * id + 2})`] = {
          color: connectorColor,
        }
      }
    })

    return styles
  },
  stepConnectorLine: {
    borderColor: 'currentColor',
    backgroundColor: 'currentColor',
    border: 0,
    height: 3,
  },
})

type Props = {
  model: BuildingEnergyModelFragment
  calibrationYear: number
  measuredHourSeries: HourSeriesFragment[]
  updatedEnergySystems: EnergySystemType[]
  onUpdateModel: (input: UpdateBuildingEnergyModelInput) => Promise<void>
}

export function CalibrationGraphsPage({
  model,
  calibrationYear,
  measuredHourSeries,
  updatedEnergySystems,
  onUpdateModel,
}: Props): React.ReactElement {
  const [selectedStep, setSelectedStep] = useState(0)
  const [isCalibrationEditing, setIsCalibrationEditing] = useState(false)

  const calScheduleSerieRef = useRef<number[]>([])

  const calibrationValuesRef = useRef<Partial<CalibrationFormValues>>(
    model.calibrationArea
      ? mapInputToCalibrationFormValues(model)
      : defaultCalibrationValues,
  )

  const chartDataRef = useRef<GraphData[]>(initGraphData(calibrationYear))

  const updatedEnergySystemsRef = useRef<EnergySystemType[]>([])

  const useStepButtonStyles = makeStyles(
    (() => {
      const styles: { [key: string]: CSSProperties } = {}

      const steps = chartDataRef.current

      steps.forEach(({ id }) => {
        if (id < steps.length) {
          let buttonColor = 'forestgreen'
          if (id < 13) buttonColor = heatingColor
          if (id > 12 && id < 16) buttonColor = coolingColor
          else if (id > 15 && id < 20) buttonColor = electricityColor

          styles[id] = {
            color: `${selectedStep !== id ? buttonColor : 'black'} !important`,
          }
        }
      })

      return styles
    })(),
  )

  const classes = useStyles()
  const stepButtonClasses = useStepButtonStyles()
  const connectorClasses = useStepConnectorStyles(chartDataRef.current)

  const { t } = useTranslation()

  const project = useReactiveVar(projectVar)

  const canEditProject = useAuthorization().canEditProject(project)

  function updateScheduleSerieValues() {
    const calScheduleFactors = calibrationScheduleFactors(
      0,
      calibrationYear,
      model.calibrationWeeklyProfile,
      model.calibrationMonthlyProfile,
    )

    calScheduleSerieRef.current = calScheduleFactors
  }

  function updateMeasuredData(stepIndex: number) {
    switch (stepIndex) {
      case 0:
      case 1:
        chartDataRef.current[stepIndex].measuredData =
          calcDhwMeasuredDataInSummerByHour(model, calScheduleSerieRef.current)
        break
      case 2:
        chartDataRef.current[stepIndex].measuredData =
          calcSumMeasuredHeatingDataInYearByMonth(model, calibrationYear)
        break
      case 3:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredHeatingInNightByEvenTempRange(
            model,
            calScheduleSerieRef.current,
            CHART_TEMP_START,
            CHART_TEMP_END,
            CHART_TEMP_STEP,
          )
        break
      case 4:
        chartDataRef.current[stepIndex].measuredData =
          calcDhwMeasuredDataInZeroTempByHour(
            model,
            calScheduleSerieRef.current,
          )
        break
      case 5:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredHeatingInDayByEvenTempRange(
            model,
            calScheduleSerieRef.current,
            CHART_TEMP_START,
            CHART_TEMP_END,
            CHART_TEMP_STEP,
          )
        break
      case 6:
        chartDataRef.current[stepIndex].measuredData =
          calcSumMeasuredHeatingDataInYearByMonth(model, calibrationYear)
        break
      case 7:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredHeatingDataInYearByHour(model)
        break
      case 8:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredHeatingDataInFirstWeekByHour(model)
        break
      case 9:
        chartDataRef.current[stepIndex].measuredData =
          calcSortedMeasuredHeatingDataInYearByHour(model)
        break
      case 10:
        chartDataRef.current[stepIndex].measuredData =
          calcDhwMeasuredDataInWinterTempByHour(
            model,
            calScheduleSerieRef.current,
          )
        break
      case 13:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredCoolingDensity(model)
        break
      case 14:
        chartDataRef.current[stepIndex].measuredData =
          calcSortedMeasuredCoolingDensityInYearByHour(model)
        break
      case 15:
        chartDataRef.current[stepIndex].measuredData =
          calcSumMeasuredCoolingDataInYearByMonth(model, calibrationYear)
        break
      case 16:
        chartDataRef.current[stepIndex].measuredData =
          calcSumMeasuredElectricityDataInYearByMonth(model, calibrationYear)
        break
      case 17:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredElectricityDataByHour(model, calScheduleSerieRef.current)
        break
      case 18:
        chartDataRef.current[stepIndex].measuredData =
          calcMeasuredElectricityInDayByEvenTempRange(
            model,
            calScheduleSerieRef.current,
            -20,
            26,
            CHART_TEMP_STEP,
          )
        break
      case 19:
        chartDataRef.current[stepIndex].measuredData =
          calcSortedMeasuredElectricityDensityInYearByHour(model)
        break
      default:
        break
    }
  }

  function updateEnergySystemData(stepIndex: number) {
    switch (stepIndex) {
      case 0:
        chartDataRef.current[stepIndex].energySystemData = [
          calcDhwCirculationLossChartData(model),
        ]
        break
      case 1:
        chartDataRef.current[stepIndex].energySystemData = [
          calcDhwCirculationLossChartData(model),
          calcDhwConsumtionInSummerByHourChartData(
            model,
            calScheduleSerieRef.current,
            calibrationYear,
          ),
        ]
        break
      case 2:
        chartDataRef.current[stepIndex].energySystemData =
          calcSumDhwHeatingInYearByMonth(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          )
        break
      case 3:
        chartDataRef.current[stepIndex].energySystemData =
          calcHeatingByEvenTempRange(
            model,
            CHART_TEMP_START,
            CHART_TEMP_END,
            CHART_TEMP_STEP,
            model.calOutdoorHourSeries?.values ?? [],
            calScheduleSerieRef.current,
            model.calibrationLimitValues?.nightStartHour ?? -1,
            model.calibrationLimitValues?.nightEndHour ?? -1,
            calibrationYear,
          )
        break
      case 4:
        chartDataRef.current[stepIndex].energySystemData = calcHeatingByHour(
          model,
          model.calOutdoorHourSeries?.values ?? [],
          calScheduleSerieRef.current,
          model.calibrationLimitValues?.zeroWeatherTempStart ?? 100,
          model.calibrationLimitValues?.zeroWeatherTempEnd ?? -100,
          calibrationYear,
        )
        break
      case 5:
        chartDataRef.current[stepIndex].energySystemData =
          calcHeatingByEvenTempRange(
            model,
            CHART_TEMP_START,
            CHART_TEMP_END,
            CHART_TEMP_STEP,
            model.calOutdoorHourSeries?.values ?? [],
            calScheduleSerieRef.current,
            model.calibrationLimitValues?.dayStartHour ?? -1,
            model.calibrationLimitValues?.dayEndHour ?? -1,
            calibrationYear,
          )
        break
      case 6:
        chartDataRef.current[stepIndex].energySystemData =
          calcSumHeatingInYearByMonth(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          )
        break
      case 7:
        chartDataRef.current[stepIndex].energySystemData = [
          calcNetHeatingInYearByHour(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          ),
        ]
        break
      case 8:
        chartDataRef.current[stepIndex].energySystemData = [
          calcNetHeatingInFirstWeekByHour(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          ),
        ]
        break
      case 9:
        chartDataRef.current[stepIndex].energySystemData = [
          calcSortedNetHeatingInYearByHour(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          ),
        ]
        break
      case 10:
        chartDataRef.current[stepIndex].energySystemData = calcHeatingByHour(
          model,
          model.calOutdoorHourSeries?.values ?? [],
          calScheduleSerieRef.current,
          model.calibrationLimitValues?.winterTempStart ?? 100,
          model.calibrationLimitValues?.winterTempEnd ?? -100,
          calibrationYear,
        )
        break
      case 11:
        chartDataRef.current[stepIndex].energySystemData =
          calcHeatingDifferencesByHour(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calScheduleSerieRef.current,
            calibrationYear,
          )
        break
      case 12:
        chartDataRef.current[stepIndex].energySystemData = [
          calcHeatingDifferenceByEvenTempRange(
            model,
            -26,
            30,
            2,
            model.calOutdoorHourSeries?.values ?? [],
            calScheduleSerieRef.current,
            calibrationYear,
          ),
        ]
        break
      case 13:
        chartDataRef.current[stepIndex].energySystemData = [
          calcNetCoolingByTempChartData(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          ),
        ]
        break
      case 14:
        chartDataRef.current[stepIndex].energySystemData = [
          calcSortedCoolingDensityInYearByHourChartData(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          ),
        ]
        break
      case 15:
        chartDataRef.current[stepIndex].energySystemData =
          calcSumCoolingInYearByMonthChartData(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          )
        break
      case 16:
        chartDataRef.current[stepIndex].energySystemData =
          calcSumElectricityInYearByMonthChartData(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          )
        break
      case 17:
        chartDataRef.current[stepIndex].energySystemData =
          calcElectricityInYearByHourChartData(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calScheduleSerieRef.current,
            calibrationYear,
          )
        break
      case 19:
        chartDataRef.current[stepIndex].energySystemData = [
          calcSortedNetElectricityDensityInYearByHourChartData(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          ),
        ]
        break
      case 20:
        chartDataRef.current[stepIndex].energySystemData =
          calcSumNetEnergyOfYear(
            model,
            model.calOutdoorHourSeries?.values ?? [],
            calibrationYear,
          )
        break
      default:
        break
    }
  }

  function resetChartData(energySystemTypes: EnergySystemType[]) {
    for (let i = 0; i < chartDataRef.current.length; i++) {
      const graphData = chartDataRef.current[i]

      if (
        graphData.dependentEnergySystems.find((d) =>
          energySystemTypes.includes(d),
        )
      ) {
        graphData.energySystemData = []
        graphData.measuredData = { id: '', data: [] }
      }
    }
  }

  const updateChartData = (stepIndex: number) => {
    resetChartData(updatedEnergySystemsRef.current)
    updatedEnergySystemsRef.current = []

    if (calScheduleSerieRef.current.length === 0) updateScheduleSerieValues()

    if (chartDataRef.current[stepIndex].measuredData.data.length === 0)
      updateMeasuredData(stepIndex)

    if (chartDataRef.current[stepIndex].energySystemData.length === 0)
      updateEnergySystemData(stepIndex)
  }

  useMemo(() => {
    updateChartData(selectedStep)
  }, [selectedStep, model]) // eslint-disable-line react-hooks/exhaustive-deps

  useMemo(() => {
    updatedEnergySystemsRef.current = updatedEnergySystems
  }, [updatedEnergySystems])

  const stepForward = () => {
    if (selectedStep === chartDataRef.current.length - 1) setSelectedStep(0)
    else setSelectedStep(selectedStep + 1)
  }

  const stepBack = () => {
    if (selectedStep === 0) setSelectedStep(chartDataRef.current.length - 1)
    else setSelectedStep(selectedStep - 1)
  }

  const onClickStep = (step: number) => {
    setSelectedStep(step)
  }

  const openCalibrationDataDialog = () => {
    setIsCalibrationEditing(true)
  }

  const closeCalibrationDataDialog = () => {
    setIsCalibrationEditing(false)
  }

  const saveCalibrationData = async (values: CalibrationFormValues) => {
    if (!_.isEqual(values, calibrationValuesRef.current)) {
      const input = mapCalibrationFormValuesToInput({ ...values })

      // Reset all calibration related data
      calScheduleSerieRef.current = []
      chartDataRef.current = initGraphData(calibrationYear)

      calibrationValuesRef.current = { ...values }

      await onUpdateModel(input)

      setSelectedStep(selectedStep)
    }

    setIsCalibrationEditing(false)
  }

  return (
    <Box flexGrow={1}>
      <Container maxWidth={false}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className={classes.header}
        >
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <Button
                className={classes.browsingButton}
                onClick={() => stepBack()}
              >
                <ChevronLeft />
              </Button>
              <Button
                className={classes.browsingButton}
                style={{ marginRight: theme.spacing(3) }}
                onClick={() => stepForward()}
              >
                <ChevronRight />
              </Button>
            </Grid>
            <Grid
              item
              xs
              zeroMinWidth
              style={{ display: 'grid', paddingRight: '20px' }}
            >
              <Typography variant="h4" noWrap>
                {`${selectedStep + 1}. ${
                  chartDataRef.current[selectedStep].name
                }`}
              </Typography>
            </Grid>
          </Grid>
          <Button
            variant="outlined"
            color="default"
            startIcon={<Settings />}
            onClick={openCalibrationDataDialog}
            disabled={!canEditProject}
          >
            {t('buildingEnergyModels.calibrationDrawer.settings')}
          </Button>
        </Box>
        <Box>
          <Stepper
            className={classes.stepper}
            nonLinear
            activeStep={selectedStep}
            connector={
              <StepConnector
                classes={{
                  root: connectorClasses.stepConnector,
                  line: connectorClasses.stepConnectorLine,
                }}
                // color="#d8d819ff"
                // style={{ backgroundColor: '#d8d819ff', height: 3 }}
              />
            }
            // alternativeLabel
          >
            {chartDataRef.current.map((step, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Step key={index} style={{ paddingRight: 0 }}>
                <Tooltip title={step.name} placement="bottom-start">
                  <StepLabel
                    StepIconProps={{
                      classes: {
                        root: stepButtonClasses[index],
                      },
                    }}
                    onClick={() => onClickStep(index)}
                    style={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              </Step>
            ))}
          </Stepper>
        </Box>
        {chartDataRef.current[selectedStep].chartType ===
          CalibrationChartType.Line && (
          <LineGraphContent
            energySystemData={
              chartDataRef.current[selectedStep].energySystemData
            }
            measuredData={chartDataRef.current[selectedStep].measuredData}
            xFormatter={chartDataRef.current[selectedStep].xFormatter}
            yFormatter={chartDataRef.current[selectedStep].yFormatter}
            axisBottomLegend={
              chartDataRef.current[selectedStep].axisBottomLegend
            }
            axisLeftLegend={chartDataRef.current[selectedStep].axisLeftLegend}
            showPoints={chartDataRef.current[selectedStep].showPoints}
            legendItemWidth={chartDataRef.current[selectedStep].legendItemWidth}
          />
        )}
        {chartDataRef.current[selectedStep].chartType ===
          CalibrationChartType.Bar && (
          <BarGraphContent
            energySystemData={
              chartDataRef.current[selectedStep].energySystemData
            }
            measuredData={chartDataRef.current[selectedStep].measuredData}
            axisBottomLegend={
              chartDataRef.current[selectedStep].axisBottomLegend
            }
            axisLeftLegend={chartDataRef.current[selectedStep].axisLeftLegend}
            legendItemWidth={chartDataRef.current[selectedStep].legendItemWidth}
            grouped={chartDataRef.current[selectedStep].grouped}
          />
        )}
      </Container>

      <CalibrationDataDialog
        open={isCalibrationEditing}
        onSubmit={saveCalibrationData}
        onClose={closeCalibrationDataDialog}
        defaultValues={calibrationValuesRef.current}
        measuredHourSeries={measuredHourSeries}
      />
    </Box>
  )
}
